import { useCallback, useEffect, useState } from 'react';

const AI_STUDIO_PROMPT_HISTORY_KEY = 'zakeke.aiStudio.promptHistory';
const AI_STUDIO_PROMPT_HISTORY_EVENT = 'zakeke-ai-studio-prompt-history-update';
const MAX_PROMPT_HISTORY_ITEMS = 10;

const readPromptHistory = (): string[] => {
    if (typeof window === 'undefined') return [];

    try {
        const storedPrompts = JSON.parse(window.localStorage.getItem(AI_STUDIO_PROMPT_HISTORY_KEY) ?? '[]');
        return Array.isArray(storedPrompts)
            ? storedPrompts.filter((storedPrompt): storedPrompt is string => typeof storedPrompt === 'string' && storedPrompt.trim().length > 0)
            : [];
    } catch {
        return [];
    }
};

const writePromptHistory = (prompts: string[]) => {
    if (typeof window === 'undefined') return;

    try {
        window.localStorage.setItem(AI_STUDIO_PROMPT_HISTORY_KEY, JSON.stringify(prompts));
        window.dispatchEvent(new CustomEvent<string[]>(AI_STUDIO_PROMPT_HISTORY_EVENT, { detail: prompts }));
    } catch {
        // localStorage can be unavailable in restricted browsing contexts.
    }
};

export const addAIStudioPromptToHistory = (prompt: string) => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) return;

    writePromptHistory([trimmedPrompt, ...readPromptHistory().filter((storedPrompt) => storedPrompt !== trimmedPrompt)].slice(0, MAX_PROMPT_HISTORY_ITEMS));
};

export const useAIStudioPromptHistory = () => {
    const [prompts, setPrompts] = useState(readPromptHistory);

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key === AI_STUDIO_PROMPT_HISTORY_KEY) setPrompts(readPromptHistory());
        };
        const handleLocalUpdate = (event: Event) => setPrompts((event as CustomEvent<string[]>).detail);

        window.addEventListener('storage', handleStorage);
        window.addEventListener(AI_STUDIO_PROMPT_HISTORY_EVENT, handleLocalUpdate);
        return () => {
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener(AI_STUDIO_PROMPT_HISTORY_EVENT, handleLocalUpdate);
        };
    }, []);

    const removePrompt = useCallback((prompt: string) => {
        writePromptHistory(readPromptHistory().filter((storedPrompt) => storedPrompt !== prompt));
    }, []);

    const clearPrompts = useCallback(() => writePromptHistory([]), []);

    return {
        prompts,
        latestPrompt: prompts[0] ?? '',
        removePrompt,
        clearPrompts
    };
};
